import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBookmarks } from '../context/BookmarksContext';
import { bookmarksApi } from '../services/api';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import PostCard from '../components/PostCard/PostCard';
import CommentCard from '../components/CommentCard/CommentCard';
import BookmarkModal from '../components/BookmarkModal/BookmarkModal';
import { buildContentPath } from '../utils/postUrls';
import './BookmarkFeed.css';

const BookmarkCardActions = ({ post, onRemove, onBookmark }) => {
  const [copied, setCopied] = useState(false);
  const contentUrl = buildContentPath(post.author, post.permlink, post);

  const txId = post.tx_id || post.txId || (() => {
    const raw = post.json_metadata || post.metadata;
    try {
      const m = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return m?.tx_id || m?.txId || null;
    } catch {
      return null;
    }
  })();

  const handleCopyLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fullUrl = `${window.location.origin}${contentUrl}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className="post-actions bookmark-post-actions">
      {/* 1. View post */}
      <Link 
        to={contentUrl} 
        className="action-btn action-link" 
        title="View post"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </Link>

      {/* 2. Bookmark */}
      <button
        type="button"
        className="action-btn"
        title="Bookmark"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onBookmark(post);
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {/* 3. Remove bookmark */}
      <button
        type="button"
        className="action-btn"
        title="Remove bookmark"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(post);
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          <line x1="21" y1="3" x2="3" y2="21" />
        </svg>
      </button>

      {/* 4. Copy link */}
      <div className="copy-link-wrapper">
        <button
          type="button"
          className="action-btn"
          title="Copy link"
          onClick={handleCopyLink}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
        {copied && (
          <div className="copy-link-notice" role="status" aria-live="polite">
            Link copied
          </div>
        )}
      </div>

      {/* 5. View tx id */}
      {txId ? (
        <a
          href={`https://hivexplorer.com/tx/${txId}`}
          className="action-btn action-link"
          target="_blank"
          rel="noopener noreferrer"
          title="View Tx ID"
          onClick={(e) => e.stopPropagation()}
        >
          TxID
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      ) : (
        <button
          type="button"
          className="action-btn"
          disabled
          title="No Tx ID available"
        >
          No Tx ID
        </button>
      )}
    </div>
  );
};

const BookmarkFeed = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { username, folderName: folderNameParam, folderId } = useParams();
  const folderName = folderNameParam ? decodeURIComponent(folderNameParam) : '';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { folders, loaded, removeBookmark, renameFolder, deleteFolder } = useBookmarks();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeBookmarkModalPost, setActiveBookmarkModalPost] = useState(null);

  const isOwner = user?.username === username || (!username && Boolean(user));
  const folder = loaded 
    ? (folderName 
        ? folders.find(f => f.name.toLowerCase() === folderName.toLowerCase()) 
        : folders.find(f => f.id === parseInt(folderId, 10))) 
    : null;

  useEffect(() => {
    if (!user) return;
    if (!loaded) return;
    if (!folder) {
      setLoading(false);
      return;
    }
    
    const fetchFeed = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await bookmarksApi.getFeed(folder.id, 50, 0, search);
        setPosts(data.posts || []);
      } catch (err) {
        setError('Failed to load bookmarks');
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchFeed();
    }, search ? 300 : 0);

    return () => clearTimeout(timer);
  }, [folder?.id, search, user, loaded, folder]);

  const handleDeleteFolder = useCallback(async () => {
    if (!folder || folder.is_default) return;
    const confirmed = window.confirm(
      `Delete bookmark folder "${folder.name}"? All saved bookmarks in this folder will also be deleted. This cannot be undone.`
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await deleteFolder(folder.id);
      navigate('/bookmarks');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete folder');
      setDeleting(false);
    }
  }, [folder, deleteFolder, navigate]);

  const handleRenameSubmit = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!folder || !trimmed || trimmed === folder.name) {
      setIsRenaming(false);
      return;
    }
    setSavingRename(true);
    try {
      await renameFolder(folder.id, trimmed);
      setIsRenaming(false);
      navigate(`/user/${user.username}/bookmarks/${encodeURIComponent(trimmed)}`, { replace: true });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename folder');
    } finally {
      setSavingRename(false);
    }
  }, [folder, renameValue, renameFolder, user?.username, navigate]);

  const handleRemoveBookmark = useCallback(async (postToRemove) => {
    if (!folder) return;
    const confirmed = window.confirm(`Remove this item from "${folder.name}"?`);
    if (!confirmed) return;
    try {
      await removeBookmark(folder.id, postToRemove.author, postToRemove.permlink);
      setPosts(prev => prev.filter(p => !(p.author === postToRemove.author && p.permlink === postToRemove.permlink)));
    } catch (err) {
      console.error('Failed to remove bookmark:', err);
      alert(err.response?.data?.error || 'Failed to remove bookmark');
    }
  }, [folder, removeBookmark]);

  return (
    <div className="bookmark-feed-page">
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      
      <div className="bookmark-feed-header">
        <div className="bookmark-feed-header-top">
          <Link to="/bookmarks" className="bookmark-feed-back">← Your Bookmarks</Link>
          <div className="bookmark-feed-header-actions">
            {isOwner && loaded && folder && !folder.is_default && (
              <>
                <button
                  type="button"
                  className="bookmark-rename-btn"
                  onClick={() => {
                    setRenameValue(folder.name);
                    setIsRenaming(true);
                  }}
                  disabled={savingRename || isRenaming}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="bookmark-delete-btn"
                  onClick={handleDeleteFolder}
                  disabled={deleting || savingRename}
                >
                  {deleting ? '…' : 'Delete folder'}
                </button>
              </>
            )}
          </div>
        </div>

        {isRenaming ? (
          <div className="bookmark-feed-rename-form">
            <input
              type="text"
              className="bookmark-feed-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              autoFocus
              disabled={savingRename}
              maxLength={100}
            />
            <button
              type="button"
              className="bookmark-rename-submit-btn"
              onClick={handleRenameSubmit}
              disabled={savingRename || !renameValue.trim() || renameValue.trim() === folder.name}
            >
              {savingRename ? '…' : 'Save'}
            </button>
            <button
              type="button"
              className="bookmark-rename-cancel-btn"
              onClick={() => setIsRenaming(false)}
              disabled={savingRename}
            >
              Cancel
            </button>
          </div>
        ) : (
          <h1 className="bookmark-feed-title">{folder ? folder.name : (folderName || 'Bookmarks')}</h1>
        )}
        
        <input 
          type="text"
          className="bookmark-search-input"
          placeholder="Search bookmarks by title or body..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="bookmark-feed-content">
        {!loaded || loading ? (
          <LoadingSpinner />
        ) : error ? (
          <p className="bookmark-error">{error}</p>
        ) : !folder ? (
          <p className="bookmark-error">Folder not found.</p>
        ) : posts.length === 0 ? (
          <p className="bookmark-empty">No bookmarks found.</p>
        ) : (
          <div className="bookmark-list">
            {posts.map(post => {
              const actions = (
                <BookmarkCardActions
                  post={post}
                  onRemove={handleRemoveBookmark}
                  onBookmark={(p) => setActiveBookmarkModalPost(p)}
                />
              );

              if (post.post_type === 'comment') {
                return (
                  <CommentCard 
                    key={`${post.author}-${post.permlink}`} 
                    post={post} 
                    customActions={actions}
                  />
                );
              }
              return (
                <PostCard 
                  key={`${post.author}-${post.permlink}`} 
                  post={post} 
                  customActions={actions}
                />
              );
            })}
          </div>
        )}
      </div>

      {activeBookmarkModalPost && (
        <BookmarkModal
          author={activeBookmarkModalPost.author}
          permlink={activeBookmarkModalPost.permlink}
          onClose={() => setActiveBookmarkModalPost(null)}
        />
      )}
    </div>
  );
};

export default BookmarkFeed;
